import {
  Button,
  Card,
  TERRA_GREEN,
  IGNIS_RED,
} from "@120wateraudit/waterworks";
import { ValidationErrors } from "final-form";
import pluralize from "pluralize";
import React, { useMemo, useEffect, useRef, useState } from "react";
import { Form } from "react-final-form";
import styled from "styled-components";

import Error from "src/components/Error";
import { isApiError } from "src/utils/apiError";
import Row from "src/components/Row";
import {
  useGetAllSubmissionPeriodsQuery,
  useAddSystemsToSubmissionPeriodMutation,
} from "src/services";
import { SubmissionPeriodType } from "src/types/enums/SubmissionPeriodType";
import { toastError } from "src/utils/toast";
import { formatSubmissionPeriodType } from "src/utils/format";
import { FormContent } from "./FormContent";
import LoadingIndicatorPage from "src/components/LoadingIndicatorPage";
import System from "src/types/System";

interface Values {
  submissionPeriodId?: number;
}

const validate = (values: Values): ValidationErrors => {
  const errors: ValidationErrors = {};
  if (!values.submissionPeriodId) {
    errors.submissionPeriodId = "A Submission Period is required";
  }

  return errors;
};

interface SubmissionModalProps {
  selectedIds: number[];
  warningCount: number;
  closeModal: () => void;
  onSelectAll: (ids: System[], removeFromSelection?: boolean) => void;
}

const AddSubmissionPeriodModal = ({
  closeModal,
  onSelectAll,
  selectedIds,
  warningCount,
}: SubmissionModalProps): JSX.Element => {
  const formValuesRef = useRef<Values | null>(null);
  const [selectedType, setSelectedType] = useState<SubmissionPeriodType | "all">("all");
  const { data: queryData, isFetching } = useGetAllSubmissionPeriodsQuery({
    filters: { active: true, locked: false },
    page: 1,
    pageSize: 100,
  });

  const allSubmissionPeriods = queryData?.data ?? [];

  // Filter submission periods by selected type
  const filteredSubmissionPeriods = useMemo(() => {
    if (selectedType === "all") {
      return allSubmissionPeriods;
    }
    return allSubmissionPeriods.filter(
      (period) => period.submissionType === selectedType
    );
  }, [allSubmissionPeriods, selectedType]);

  // Sort submission periods to put active ones first
  const sortedSubmissionPeriods = [...filteredSubmissionPeriods].sort((a, b) => {
    // Second priority: sandbox=true comes last
    if (!a.sandbox && b.sandbox) return -1;
    if (a.sandbox && !b.sandbox) return 1;

    return 0;
  });

  // Get unique types from available periods
  const availableTypes = useMemo(() => {
    const types = new Set<SubmissionPeriodType>();
    allSubmissionPeriods.forEach((period) => {
      types.add(period.submissionType);
    });
    return Array.from(types);
  }, [allSubmissionPeriods]);

  const typeOptions = [
    { key: "all", value: "all", text: "All Types" },
    ...availableTypes.map((type) => ({
      key: type,
      value: type,
      text: formatSubmissionPeriodType(type),
    })),
  ];

  const firstSubmissionPeriodId = sortedSubmissionPeriods[0]?.id;

  const initialValues: Values = useMemo(
    () => ({
      submissionPeriodId: firstSubmissionPeriodId ?? undefined,
    }),
    [firstSubmissionPeriodId]
  );

  const [addSystems, { data, error, isError, isLoading }] =
    useAddSystemsToSubmissionPeriodMutation();

  const { data: responseData } = data ?? {};
  const { systemsNotUpdated, updatedSystems } = responseData ?? {};
  const systemsUpdatedCount = updatedSystems?.length ?? 0;
  const updatedCountText = pluralize("Utility", systemsUpdatedCount, true);

  const submit = async (values: Values): Promise<void> => {
    formValuesRef.current = values;

    const { submissionPeriodId } = values ?? {};
    try {
      if (!submissionPeriodId) {
        return;
      }
      await addSystems({
        submissionPeriodId,
        systemIds: selectedIds,
      }).unwrap();
    } catch {
      // TODO better error message
      toastError("Submission Periods could not be assigned. Please try again.");
    }
  };

  useEffect(() => {
    if (updatedSystems) {
      onSelectAll(updatedSystems, true);
    }
  }, [updatedSystems, onSelectAll]);

  const errors = useMemo(() => {
    if (!error || !isError) {
      return undefined;
    }

    if (isApiError(error)) {
      return [error.data?.error];
    }

    return ["Please try again or contact support@120water.com"];
  }, [error, isError]);

  if (isFetching || isLoading) {
    return <LoadingIndicatorPage />;
  }

  return (
    <ModalContentContainer>
      <Form
        initialValues={formValuesRef.current ?? initialValues}
        onSubmit={submit}
        render={({ valid, handleSubmit, submitting }) => {
          return (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit();
              }}
            >
              <FormContentContainer>
                <h1 style={{ marginBottom: "20px" }}>
                  {`Assign Submission Period to ${pluralize(
                    "Utilities",
                    warningCount,
                    true
                  )}`}
                </h1>
                {sortedSubmissionPeriods.length === 0 && (
                  <h3>No Submission Periods to assign to currently.</h3>
                )}
                {sortedSubmissionPeriods.length > 0 && (
                  <FormContent
                    submissionPeriods={sortedSubmissionPeriods}
                    warningCount={warningCount}
                    selectedType={selectedType}
                    typeOptions={typeOptions}
                    onTypeChange={setSelectedType}
                  />
                )}
              </FormContentContainer>
              {responseData && (
                <>
                  {updatedSystems && (
                    <SuccessCard>
                      {systemsUpdatedCount === 0
                        ? "No Utilities were updated."
                        : `Successfully assigned ${updatedCountText}`}
                    </SuccessCard>
                  )}
                  {systemsNotUpdated && systemsNotUpdated.length > 0 && (
                    <FailureCard>
                      {systemsNotUpdated.map((text: string, index: number) => (
                        <p key={`system_not_updated${index}`}>{text}</p>
                      ))}
                    </FailureCard>
                  )}
                </>
              )}
              <Row
                style={{
                  gap: "8px",
                  justifyContent: "flex-end",
                  marginRight: "1.5em",
                }}
              >
                <Button onClick={closeModal}>Cancel</Button>
                <Button
                  type="submit"
                  disabled={submitting || isLoading || !valid}
                  variant="primary"
                >
                  Assign
                </Button>
              </Row>
              {errors && (
                <Error style={{ margin: "12px 24px" }} messages={errors} />
              )}
            </form>
          );
        }}
        validate={validate}
      />
    </ModalContentContainer>
  );
};

const ModalContentContainer = styled.div`
  margin: 2rem;
`;

const FormContentContainer = styled.div`
  text-align: center;
  margin: 1.5rem;
`;

const SuccessCard = styled(Card)`
  background-color: ${TERRA_GREEN};
  margin-bottom: 10px;
`;

const FailureCard = styled(Card)`
  background-color: ${IGNIS_RED};
  margin-bottom: 20px;
`;

export default AddSubmissionPeriodModal;
